import { useMemo, useRef, useState } from "react";
import "./DownloadPage.css";

/**
 * Placeholder data — replace with Supabase results later.
 * Each album has: id, title, date, coverUrl, and tracks[]
 * Each track has: id, title, date, audioUrl.
 */
const placeholderAlbums = [
  {
    id: "album-1",
    title: "Blood Downloads — Vol. 1",
    date: "2025-09-15",
    coverUrl:
      "https://images.unsplash.com/photo-1511379938547-c1f69419868d?q=80&w=800&auto=format&fit=crop",
    tracks: [
      {
        id: "a1t1",
        title: "Crimson Overture",
        date: "2025-09-15",
        audioUrl:
          "https://www2.cs.uic.edu/~i101/SoundFiles/StarWars60.wav", // placeholder WAV
      },
      {
        id: "a1t2",
        title: "Pulse & Echo",
        date: "2025-09-15",
        audioUrl:
          "https://www2.cs.uic.edu/~i101/SoundFiles/CantinaBand60.wav",
      },
      {
        id: "a1t3",
        title: "Midnight Serum",
        date: "2025-09-15",
        audioUrl:
          "https://www2.cs.uic.edu/~i101/SoundFiles/ImperialMarch60.wav",
      },
    ],
  },
  {
    id: "album-2",
    title: "Blood Downloads — Vol. 2",
    date: "2025-10-01",
    coverUrl:
      "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?q=80&w=800&auto=format&fit=crop",
    tracks: [
      {
        id: "a2t1",
        title: "Veins of Neon",
        date: "2025-10-01",
        audioUrl:
          "https://www2.cs.uic.edu/~i101/SoundFiles/Theme60.wav",
      },
      {
        id: "a2t2",
        title: "Scarlet Horizon",
        date: "2025-10-01",
        audioUrl:
          "https://www2.cs.uic.edu/~i101/SoundFiles/PinkPanther60.wav",
      },
    ],
  },
];

type Track = {
  id: string;
  title: string;
  date: string;
  audioUrl: string;
};

type Album = {
  id: string;
  title: string;
  date: string;
  coverUrl: string;
  tracks: Track[];
};

export default function DownloadsPage() {
  // you’ll replace this with your Supabase fetch later
  const albums: Album[] = useMemo(() => placeholderAlbums, []);

  // track which track is playing globally (only one at a time)
  const [playingTrackId, setPlayingTrackId] = useState<string | null>(null);
  // map of trackId -> audio element refs (so we can control them)
  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({});

  const handlePlay = (track: Track) => {
    // stop any currently playing track first
    if (playingTrackId && playingTrackId !== track.id) {
      const prev = audioRefs.current[playingTrackId];
      if (prev) {
        prev.pause();
        prev.currentTime = 0;
      }
    }
    const el = audioRefs.current[track.id];
    if (el) {
      el.play().catch(() => {
        // ignore autoplay restrictions — user is clicking anyway
      });
      setPlayingTrackId(track.id);
    }
  };

  const handleStop = (track: Track) => {
    const el = audioRefs.current[track.id];
    if (el) {
      el.pause();
      el.currentTime = 0;
    }
    if (playingTrackId === track.id) setPlayingTrackId(null);
  };

  const handleEnded = (trackId: string) => {
    if (playingTrackId === trackId) setPlayingTrackId(null);
  };

  return (
    <main className="downloads-wrap">
      <header className="downloads-header">
        <h1>Downloads</h1>
        <p className="sub">Albums with tracklists — click to play or download.</p>
      </header>

      {/* albums grid */}
      <section className="album-grid">
        {albums.map((album) => (
          <article key={album.id} className="album-card">
            <div className="album-cover">
              <img src={album.coverUrl} alt={`${album.title} cover`} />
            </div>
            <div className="album-meta">
              <h2 className="album-title">{album.title}</h2>
              <div className="album-date">{formatDate(album.date)}</div>
            </div>

            <details className="album-details" open>
              <summary className="album-summary">Tracklist</summary>
              <ul className="track-list">
                {album.tracks.map((t) => (
                  <li key={t.id} className="track-item">
                    <div className="track-main">
                      <div className="track-titles">
                        <span className="track-title">{t.title}</span>
                        <span className="track-date">{formatDate(t.date)}</span>
                      </div>
                      <div className="track-actions">
                        <button
                          className="btn play"
                          onClick={() => handlePlay(t)}
                          aria-label={`Play ${t.title}`}
                        >
                          ▶ Play
                        </button>
                        <button
                          className="btn stop"
                          onClick={() => handleStop(t)}
                          aria-label={`Stop ${t.title}`}
                        >
                          ■ Stop
                        </button>
                        <a
                          className="btn download"
                          href={t.audioUrl}
                          download
                          aria-label={`Download ${t.title}`}
                        >
                          ⬇ Download
                        </a>
                      </div>
                    </div>

                    {/* hidden audio element (controlled) */}
<audio
  preload="metadata"
  ref={(el) => { audioRefs.current[t.id] = el; }}  // <- returns void
  src={t.audioUrl}
  onEnded={() => handleEnded(t.id)}
/>

                  </li>
                ))}
              </ul>
            </details>
          </article>
        ))}
      </section>
    </main>
  );
}

function formatDate(s: string) {
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}
